// Small shared pieces: stat cards and the loading, error and empty states.

import type { ReactNode } from 'react';

export function Stat({ label, value, note, href, accent, warn }: { label: string; value: ReactNode; note?: ReactNode; href?: string; accent?: boolean; warn?: boolean }) {
  const inner = (
    <>
      <span className="stat-label">{label}</span>
      <span className={warn ? 'stat-value warn' : 'stat-value'}>{value}</span>
      {note !== undefined && <span className={accent ? 'stat-note accent' : 'stat-note'}>{note}</span>}
    </>
  );
  return href ? (
    <a className="stat" href={href}>
      {inner}
    </a>
  ) : (
    <div className="stat">{inner}</div>
  );
}

export type LoadingKind = 'home' | 'cards' | 'table' | 'list';

/** Grey placeholder shapes in the layout the screen is about to show, so nothing jumps when data arrives. */
export function Loading({ kind = 'list', what = 'Loading' }: { kind?: LoadingKind; what?: string }) {
  return (
    <div className="skeleton" role="status" aria-live="polite">
      <span className="visually-hidden">{what}</span>
      {(kind === 'home' || kind === 'table') && (
        <div className={kind === 'home' ? 'stats' : 'stats tight'} aria-hidden="true">
          {[0, 1, 2, 3].map((i) => (
            <div className="stat sk-stat" key={i}>
              <span className="sk sk-line" style={{ width: '55%' }} />
              <span className="sk sk-num" />
              {kind === 'home' && <span className="sk sk-line" style={{ width: '40%' }} />}
            </div>
          ))}
        </div>
      )}
      {kind === 'home' && (
        <div className="sk-split" aria-hidden="true">
          {[0, 1].map((i) => (
            <div className="card" key={i}>
              <div className="sk-head"><span className="sk sk-title" /></div>
              {[0, 1, 2].map((j) => <SkRow key={j} />)}
            </div>
          ))}
        </div>
      )}
      {kind === 'cards' &&
        [0, 1].map((i) => (
          <div className="card sk-card" key={i} aria-hidden="true">
            <div className="sk-head"><span className="sk sk-pill" /><span className="sk sk-title" /></div>
            <div className="sk-sides">
              {[0, 1].map((j) => (
                <div key={j} className="sk-side">
                  <span className="sk sk-line" style={{ width: '30%' }} />
                  <SkRow /><SkRow />
                </div>
              ))}
            </div>
          </div>
        ))}
      {(kind === 'table' || kind === 'list') && (
        <div className="card" aria-hidden="true">
          {kind === 'table' && <div className="sk-head"><span className="sk sk-line" style={{ width: '20%' }} /></div>}
          {[0, 1, 2, 3, 4, 5].map((j) => <SkRow key={j} />)}
        </div>
      )}
    </div>
  );
}

function SkRow() {
  return (
    <div className="sk-row">
      <span className="sk sk-date" />
      <span className="sk sk-line" style={{ flex: 1 }} />
      <span className="sk sk-amount" />
    </div>
  );
}

/** Shown before sign-in finishes: the app's frame with placeholders, instead of a bare "Loading". */
export function BootScreen() {
  return (
    <div className="shell">
      <nav aria-label="Main" className="side">
        <div className="brand">
          <strong>Four Winds</strong>
          <span>Payment matching</span>
        </div>
        <div className="side-nav" aria-hidden="true">
          {[0, 1, 2, 3, 4].map((i) => <span key={i} className="sk sk-nav" />)}
        </div>
      </nav>
      <main className="main">
        <div className="page">
          <span className="sk sk-h1" aria-hidden="true" />
          <Loading kind="home" what="Signing in" />
        </div>
      </main>
    </div>
  );
}

export function ErrorBanner({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="banner error" role="alert">
      <div>
        <strong>Couldn't load this</strong>
        <span>{message}</span>
      </div>
      {onRetry && (
        <button type="button" className="btn" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="state">{children}</p>;
}
